import { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import API from "../services/api";
import "./PublicMatchesPage.css";
import AnalysisRenderer from "../components/analysis-editor/AnalysisRenderer";
import socket from "../socket";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faStar, faBell, faXmark } from "@fortawesome/free-solid-svg-icons";

import goalSound from "../sounds/goal.wav";
import cancelSound from "../sounds/cancel.wav";
import confidenceSound from "../sounds/confidence.wav";
import finishSound from "../sounds/finish.wav";
import vipSound from "../sounds/finish.wav"; 
import redCardSound from "../sounds/redcard.wav";
import MatchStatsModal from "./MatchStatsModal";
import MatchStandingsTab from "./MatchStandingsTab";

function PublicMatchesPage() {
  const [user, setUser] = useState(() => JSON.parse(localStorage.getItem("user")));
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPrediction, setSelectedPrediction] = useState("resultat");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);

  const [countryFilter, setCountryFilter] = useState("");
  const [sortType, setSortType] = useState("heure");
  const [matchFilter, setMatchFilter] = useState("default");
  const [showMobileSignals, setShowMobileSignals] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [openedAnalysis, setOpenedAnalysis] = useState(null);
  const [showVipModal, setShowVipModal] = useState(false);

  const [signals, setSignals] = useState([]);
  const previousMatchesRef = useRef([]);
  const [favorites, setFavorites] = useState([]);
  const [showAllMatches, setShowAllMatches] = useState(false);
  const [loginModalType, setLoginModalType] = useState("favorite");

  const [selectedMatch, setSelectedMatch] = useState(null);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [buttonEffect, setButtonEffect] = useState({ type: "", until: 0 });

  const soundEnabledRef = useRef(true);

  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);

  const openStats = (match) => {
    console.log("MATCH CLIQUÉ :", match);
    setSelectedMatch(match);
    setShowStatsModal(true);
  };

  const closeStats = () => {
    setShowStatsModal(false);
    setSelectedMatch(null);
  };

  const goalAudio = useRef(new Audio(goalSound));
  const cancelAudio = useRef(new Audio(cancelSound));
  const confidenceAudio = useRef(new Audio(confidenceSound));
  const finishAudio = useRef(new Audio(finishSound));
 

  const predictionLabels = {
    resultat: "Résultat du Match",
    doubleChance: "Double Chance",
    btts: "BTTS",
    plusMoins: "Plus / Moins",
    mtPlus: "Mi-temps + Buts",
    mtFin: "Mi-temps / Fin",
    scoreMt: "Score Exact MT",
    scoreExact: "Score Exact FT"
  };

  useEffect(() => {
    const activeUser = JSON.parse(localStorage.getItem("user"));
    if (activeUser) {
      setUser(activeUser);
      socket.emit("registerUser", activeUser.id);
    }
  }, []);

  useEffect(() => {
    fetchMatches();
    const interval = setInterval(() => {
      fetchMatches();
    }, 30000); 
    return () => clearInterval(interval);
  }, [selectedDate]);

  useEffect(() => {
    socket.on("matchUpdated", (updatedMatch) => {
      console.log("LIVE MATCH UDPATE :", updatedMatch);
      setMatches((prevMatches) =>
        prevMatches.map((match) =>
          match._id === updatedMatch._id || match.fixtureId === updatedMatch.fixtureId
            ? { ...match, ...updatedMatch }
            : match
        )
      );
    });

    socket.on("favoriteSignal", (signal) => {
      console.log("SIGNAL FRONT :", signal);
      console.log(signal.player)

      const formattedSignal = {
        ...signal,
        type: signal.type || (signal.detail?.includes("Red") ? "red_card" : "info"),
        message: signal.message || `🟥 CARTON ROUGE : ${
          signal.player?.name || signal.player.name || "-"
        } (${
          signal.team?.name || signal.team || "-"
        })`,
        minute: signal.minute || signal.elapsed || signal.time?.elapsed || "?",
        time: signal.time_string || new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };

      setSignals((prev) => [formattedSignal, ...prev].slice(0, 20));

      if (Notification.permission === "granted") {
        new Notification("FootVision", {
          body: formattedSignal.message,
          icon: "/favicon.ico"
        });
      }

      if (soundEnabledRef.current) {
        if (formattedSignal.type === "red_card" || signal.type === "red_card") {
          new Audio(redCardSound).play().catch(() => {});
        } else {
          switch (signal.type) {
            case "goal": new Audio(goalSound).play().catch(() => {}); break;
            case "vip_signal": new Audio(vipSound).play().catch(() => {}); break;
            case "finished": new Audio(finishSound).play().catch(() => {}); break;
            default: break;
          }
        }
      }
    });

    socket.on("newSignal", (signal) => {
      console.log("Nouveau signal global :", signal);
      setSignals((prev) => [signal, ...prev]);
    });

    return () => {
      socket.off("matchUpdated");
      socket.off("favoriteSignal");
      socket.off("newSignal");
    };
  }, []);

  const openAnalysis = (match) => {
    const token = localStorage.getItem("token");
    const freshUser = JSON.parse(localStorage.getItem("user"));

    if (match.accessLevel !== "vip") {
      setOpenedAnalysis(match);
      return;
    }
    if (!token) {
      setLoginModalType("vip");
      setShowLoginModal(true);
      return;
    }
    if (!freshUser?.isVip) {
      setShowVipModal(true);
      return;
    }
    setOpenedAnalysis(match);
  };

  const toggleSound = () => {
    const newValue = !soundEnabled;
    setSoundEnabled(newValue);
    localStorage.setItem("signalSound", newValue ? "on" : "off");
  };

  const toggleFavorite = async (matchId) => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoginModalType("favorite");
      setShowLoginModal(true);
      return;
    }
    try {
      const response = await API.post(`/users/favorites/${matchId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.favorite) {
        setFavorites((prev) => [...prev, matchId]);
      } else {
        setFavorites((prev) => prev.filter((id) => id !== matchId));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const playSound = (audioRef) => {
    if (!soundEnabled) return;
    audioRef.current.currentTime = 0;
    audioRef.current.play().catch(() => {});
  };

  const playSignalSound = (type) => {
    if (!soundEnabledRef.current) return;

    switch (type) {
      case "goal":
        playSound(goalAudio);
        break;
      case "cancelled":
        playSound(cancelAudio);
        break;
      case "confidence":
        playSound(confidenceAudio);
        break;
      case "finished":
        playSound(finishAudio);
        break;
      default:
        break;
    }
  };

  const fetchMatches = async () => {
    try {
      const response = await API.get(`/matches/public?date=${selectedDate}`);
      const newMatches = response.data;
      const oldMatches = previousMatchesRef.current;
      const newSignals = [];

      newMatches.forEach((newMatch) => {
        const oldMatch = oldMatches.find((m) => m._id === newMatch._id);
        if (!oldMatch) return;

        const oldTotal = (oldMatch.goalsHome || 0) + (oldMatch.goalsAway || 0);
        const newTotal = (newMatch.goalsHome || 0) + (newMatch.goalsAway || 0);

        if (oldTotal !== newTotal) {
          if (newTotal > oldTotal) {
            newMatch.scoreEffect = "goal-flash";
            newMatch.scoreEffectUntil = Date.now() + 15000;
            newSignals.unshift({
              type: "goal",
              country: newMatch.country,
              league: newMatch.league,
              minute: newMatch.elapsed,
              message: `⚽ BUT : ${newMatch.homeTeam} ${newMatch.goalsHome} - ${newMatch.goalsAway} ${newMatch.awayTeam}`,
              time: new Date().toLocaleTimeString()
            });
            playSignalSound("goal");
          } else {
            newMatch.scoreEffect = "cancelled-flash";
            newMatch.scoreEffectUntil = Date.now() + 15000;
            newSignals.unshift({
              type: "cancelled",
              country: newMatch.country,
              league: newMatch.league,
              minute: newMatch.elapsed,
              message: `❌ BUT ANNULÉ : ${newMatch.homeTeam} ${newMatch.goalsHome}-${newMatch.goalsAway} ${newMatch.awayTeam}`,
              time: new Date().toLocaleTimeString()
            });
            playSignalSound("cancelled");
          }
        }

        if (oldMatch.confidence !== newMatch.confidence) {
          newSignals.unshift({
            type: "confidence",
            country: newMatch.country,
            league: newMatch.league,
            message: `📈 ${newMatch.homeTeam} vs ${newMatch.awayTeam} → Fiabilité ${newMatch.confidence}%`,
            time: new Date().toLocaleTimeString()
          });
          playSignalSound("confidence");
        }

        const oldRedCards = oldMatch.events 
          ? oldMatch.events.filter(e => e.type === "Card" && (e.detail === "Red Card" || e.detail === "Second Yellow Card")).length 
          : 0;

        const newRedCards = newMatch.events 
          ? newMatch.events.filter(e => e.type === "Card" && (e.detail === "Red Card" || e.detail === "Second Yellow Card")).length 
          : 0;

        if (newRedCards > oldRedCards) {
          const latestRedCardEvent = newMatch.events
            .filter(e => e.type === "Card" && (e.detail === "Red Card" || e.detail === "Second Yellow Card"))
            .pop();

          const playerName = latestRedCardEvent?.player?.name || "Joueur";
          const teamName = latestRedCardEvent?.team?.name || "Équipe";

          newSignals.unshift({
            type: "red_card",
            country: newMatch.country,
            league: newMatch.league,
            minute: latestRedCardEvent?.time?.elapsed || newMatch.elapsed || "?",
            message: `🟥 CARTON ROUGE : ${playerName} (${teamName}) - ${newMatch.homeTeam} vs ${newMatch.awayTeam}`,
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          });

          if (soundEnabledRef.current) {
            new Audio(redCardSound).play().catch(() => {});
          }
        }

        if (oldMatch.liveStatus !== "FT" && newMatch.liveStatus === "FT") {
          newSignals.unshift({
            type: "finished",
            country: newMatch.country,
            league: newMatch.league,
            message: `🏁 Match terminé : ${newMatch.homeTeam} ${newMatch.goalsHome}-${newMatch.goalsAway} ${newMatch.awayTeam}`,
            time: new Date().toLocaleTimeString()
          });
          playSignalSound("finished");
        }
      });

      if (newSignals.length > 0) {
        setSignals((prev) => [...newSignals, ...prev].slice(0, 20));

        const prioritySignal = newSignals.find(s => s.type === "goal") || 
                               newSignals.find(s => s.type === "cancelled") || 
                               newSignals.find(s => s.type === "red_card") || 
                               newSignals[0];

        if (prioritySignal) {
          setButtonEffect({
            type: prioritySignal.type,
            until: Date.now() + 15000
          });
        }
      }

      previousMatchesRef.current = newMatches;
      setMatches(newMatches);
    } catch (error) {
      console.error(error);
    } {
      setLoading(false);
    }
  };

  const getStatusClass = (status) => {
    if (status === "reussi") return "status-reussi";
    if (status === "echec") return "status-echec";
    return "status-encours";
  };

  if (loading) {
    return (
      <div className="conteneur-grid">
        <div className="grid">
          {[1, 2, 3].map((n) => (
            <div key={n} className="card">
              <div className="card__title skeleton"></div>
              <div className="card__meta skeleton"></div>
              <div className="card__buttons">
                <button className="skeleton"></button>
                <button className="skeleton"></button>
                <button className="skeleton"></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const liveStatuses = ["1H", "HT", "2H", "ET", "BT", "P"];
  const finishedStatuses = ["FT", "AET", "PEN"];
  const notStartedStatuses = ["NS", "TBD"];

  const filteredMatches = matches.filter((match) => {
    if (countryFilter && match.country !== countryFilter) return false;
    if (matchFilter === "favorites") return favorites.includes(match._id);
    if (matchFilter === "live") return liveStatuses.includes(match.liveStatus);
    if (matchFilter === "finished") return finishedStatuses.includes(match.liveStatus);
    if (matchFilter === "not_started") return notStartedStatuses.includes(match.liveStatus);
    if (matchFilter === "default") {
      return liveStatuses.includes(match.liveStatus) || notStartedStatuses.includes(match.liveStatus);
    }
    return true;
  });

  const sortedMatches = [...filteredMatches].sort((a, b) => {
    if (sortType === "pays") return a.country.localeCompare(b.country);
    return new Date(a.matchDate) - new Date(b.matchDate);
  });

  const displayedMatches = showAllMatches ? sortedMatches : sortedMatches.slice(0, 10);

  return (
    <div className="public-page">
      <Helmet>
        <title>Matchs du Jour, Live Scores & analyses algorithmiques | FootVision Live</title>
        <meta name="description" content="Suivez le calendrier complet des matchs de football aujourd'hui. Scores en direct, compositions d'équipes et indices de fiabilité chiffrés sur FootVision." />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Matchs de Football en Direct, Pronostics & Analyses – FootVision Live" />
        <meta property="og:description" content="Accédez instantanément aux flux statistiques et aux signaux d'alertes en temps réel." />
        <meta property="og:url" content="https://www.footvisionlive.com/matches" />
        <meta property="og:image" content="https://www.footvisionlive.com/logo2.png" />
      </Helmet>

      <div className="conteneur">
        <div className="filters">
          <div className="titre-filtre">Calendrier</div>
          <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />

          <div className="titre-filtre">Filtrer par :</div>
          <select value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)}>
            <option value="">Tous pays</option>
            {[...new Set(matches.map((m) => m.country))].sort().map((country) => (
              <option key={country} value={country}>{country}</option>
            ))}
          </select>

          <div className="titre-filtre">Trier par :</div>
          <select value={sortType} onChange={(e) => setSortType(e.target.value)}>
            <option value="heure">Heure</option>
            <option value="pays">Pays A-Z</option>
          </select>

          <select 
            className="prediction-select-mobile" 
            value={selectedPrediction} 
            onChange={(e) => setSelectedPrediction(e.target.value)}
          >
            {Object.entries(predictionLabels).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>

          <div className="titre-types">Types de pronostics</div>
          <div className="prediction-tabs">
            {Object.entries(predictionLabels).map(([key, label]) => (
              <button 
                key={key} 
                className={selectedPrediction === key ? "active-tab" : ""} 
                onClick={() => setSelectedPrediction(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="matches-container">
          <header className="page-header">
            <div className="match-filter-buttons">
              <button className={matchFilter === "default" ? "active-filter" : ""} onClick={() => setMatchFilter("default")}>Par défaut</button>
              <button className={matchFilter === "live" ? "active-filter btn-live" : ""} onClick={() => setMatchFilter("live")}>🔴 En cours</button>
              <button className={matchFilter === "not_started" ? "active-filter" : ""} onClick={() => setMatchFilter("not_started")}>🟢 Non commencés</button>
              <button className={matchFilter === "finished" ? "active-filter" : ""} onClick={() => setMatchFilter("finished")}>⚫ Terminés</button>
              <button className={matchFilter === "favorites" ? "active-filter btn-fav" : ""} onClick={() => setMatchFilter("favorites")}>⭐ Favoris ({favorites.length})</button>
            </div>
          </header>
          <div className="matches-count">
            <span className="important">{sortedMatches.length}</span> - matchs disponibles
          </div>

          {sortedMatches.length === 0 && <div className="no-match">Aucun match disponible</div>}

          {displayedMatches.map((match) => {
            const prediction = match.predictions?.[selectedPrediction] || null;

            const homeRedCards = match.events
              ? match.events.filter(
                  (event) =>
                    event.team === match.homeTeam &&
                    event.type === "Card" &&
                    (event.detail === "Red Card" || event.detail === "Second Yellow Card")
                ).length
              : 0;

            const awayRedCards = match.events
              ? match.events.filter(
                  (event) =>
                    event.team === match.awayTeam &&
                    event.type === "Card" &&
                    (event.detail === "Red Card" || event.detail === "Second Yellow Card")
                ).length
              : 0;

            return (
              <div key={match._id} className="match-card">
                <div className="favorite-btn" onClick={() => toggleFavorite(match._id)}>
                  <FontAwesomeIcon icon={faStar} style={{ color: favorites.includes(match._id) ? "var(--mt)" : "gray" }} />
                </div>
                
                <div className="league">{match.country} - {match.league}</div>

                <div className="teams">
                  <div className="team">
                    {match.homeTeam} 
                    {homeRedCards > 0 && <span className="red-card-badge">🟥 {homeRedCards} </span>}
                  </div>
                  <div className="vs">-</div>
                  <div className="team">
                    {awayRedCards > 0 && <span className="red-card-badge">{awayRedCards} 🟥 </span>}
                    {match.awayTeam}
                  </div>
                </div>

                <div className="statut-conteneur">
                                   
                    <div className={`match-live-box ${match.scoreEffectUntil > Date.now() ? match.scoreEffect : ""}`} onClick={() => openStats(match)}>
                    
                      <div className="match-time">
                      🕒 {new Date(match.matchDate).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </div>
           {/*
              <p style={{fontSize:"14px", padding: "10px"}}> Rechargement de score en direct...</p>
      
             */}

                    <div className={`match-status ${match.liveStatus}`}>
                      {match.liveStatus === "NS" && "🟢 NS"}
                      {match.liveStatus === "TBD" && "🟢 TBD"}
                      {match.liveStatus === "1H" && `🔴 ${match.elapsed}'`}
                      {match.liveStatus === "HT" && "🟡 MT"}
                      {match.liveStatus === "2H" && `🔴 ${match.elapsed}'`}
                      {match.liveStatus === "ET" && `purple PRO ${match.elapsed}'`}
                      {match.liveStatus === "BT" && "🟣 Pause PRO"}
                      {match.liveStatus === "P" && "🟣 TAB"}
                      {match.liveStatus === "FT" && "⚫ FT"}
                      {match.liveStatus === "AET" && "⚫ AP"}
                      {match.liveStatus === "PEN" && "⚫ TAB"}
                    </div>

                    {!notStartedStatuses.includes(match.liveStatus) && (
                      <div className="live-score">
                        <span>{match.goalsHome ?? 0}</span> - <span>{match.goalsAway ?? 0}</span>
                      </div>
                    )}

                    {[ "HT", "2H", "ET", "BT", "P", "FT", "AET", "PEN" ].includes(match.liveStatus) && (
                      <div className="halftime-score">
                        MT : {match.htGoalsHome} - {match.htGoalsAway}
                      </div>
                    )}
            

                    <FontAwesomeIcon icon={faChevronDown} style={{ marginLeft: 8, marginBottom: 3, color: "var(--orange)" }} />
                  </div>
  
                      
                </div>

                <div className="prediction-box">
                  <h3>{predictionLabels[selectedPrediction]}</h3>
                  {match.accessLevel === "vip" && !user?.isVip ? (
                    <div className="prediction vip-prediction">🔒 Pronostic VIP</div>
                  ) : (
                    <div className={`prediction ${getStatusClass(prediction?.status)}`}>
                      {prediction?.value && prediction.value.trim() !== "" ? prediction.value : "Aucun pronostic"}
                    </div>
                  )}
                </div>

                <div className="confidence-container">
                  <div className="confidence-header">
                    <span>Fiabilité : </span>
                    <strong>{match.confidence || 0}%</strong>
                       |
                       <strong>Cotes : </strong>
                    { (
                      <span >
                        {prediction?.odd && prediction.value.trim() !== "" ? prediction.odd : "-"}
                      </span>
                    )}
                  </div>
                  <div className="confidence-bar">
                    <div
                      className="confidence-fill"
                      style={{
                        width: `${match.confidence || 0}%`,
                        background: match.confidence >= 80 ? "var(--barre-progress)" : match.confidence >= 60 ? "var(--encours)" : "var(--danger)"
                      }}
                    />
                  </div>
                </div>

                <div className="analysis-btn">
                  <button className="open-analysis-btn" onClick={() => openAnalysis(match)}>
                    {match.accessLevel === "vip" && !user?.isVip ? "🔒 Débloquer l'analyse VIP" : "Voir l'analyse complète"}
                    <FontAwesomeIcon icon={faChevronDown} style={{ marginLeft: 8 }} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className={`signals-section ${showMobileSignals ? "mobile-open" : ""}`}>
          <div className="signals-header">
            <h3>🚨 Derniers Signaux</h3>
            <button className="sound-toggle-btn" onClick={toggleSound}>
              {soundEnabled ? "🔊" : "🔇"}
            </button>
            <button className="close-signals-btn" onClick={() => setShowMobileSignals(false)}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>

          {signals.length === 0 ? (
            <div className="no-signal">Aucun signal</div>
          ) : (
            <div className="no-signal">
            {signals.map((signal, index) => (
              <div key={index} className={`signal-card ${signal.type}`}>
                <div className="signal-meta">
                  <span>🌍 {signal.country}</span>
                  <span>🏆 {signal.league}</span>
                </div>
                <div className="signal-card-content">
                  <div className="signal-message">{signal.message}</div>
                </div>
                <div className="signal-minute">⏱ {signal.minute}'</div>
                <div className="signal-time">{signal.time}</div>
              </div>
            ))}
            </div>
          )}
        </div>
      </div>

      {openedAnalysis && (
        <div className="analysis-modal">
          <div className="analysis-modal-content">
            <button className="close-analysis" onClick={() => setOpenedAnalysis(null)}>✕</button>
            <div className="analysis-modal-teams">
              <div className="analysis-modal-team"> {openedAnalysis.homeTeam} </div> vs <div className="analysis-modal-team"> {openedAnalysis.awayTeam} </div>
            </div>
            <div className="analysis-modal-league">
              {openedAnalysis.country} - {openedAnalysis.league}
            </div>
            <div className="analysis-modal-time">
              <p>🕒 {new Date(openedAnalysis.matchDate).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
              <div className="fullscreen-score">
                
              </div>
            </div> 
            
            <div className="analysis-tabs">
              <MatchStandingsTab matchId={openedAnalysis?._id} />
            </div> 

            <br />
            <h2 className="analyse-avancee">Analyses avancées </h2>
            <AnalysisRenderer blocks={openedAnalysis.analysisBlocks} />
          </div>
        </div>
      )}

      {sortedMatches.length > 10 && (
        <div className="show-all-container">
          <button className="show-all-btn" onClick={() => setShowAllMatches(!showAllMatches)}>
            {showAllMatches ? "Voir moins" : `Voir tout (${sortedMatches.length})`}
          </button>
        </div>
      )}

      {showLoginModal && (
        <div className="login-modal-overlay">
          <div className="login-modal">
            <div className="login-modal-icon">{loginModalType === "favorite" ? "⭐" : "📊"}</div>
            <h2>{loginModalType === "favorite" ? "Ajouter aux favoris" : loginModalType === "vip" ? "Analyse VIP" : "Connexion requise"}</h2>
            <p>{loginModalType === "favorite" ? "Connectez-vous pour suivre vos matchs favoris et recevoir les alertes en direct." : "Cette analyse est réservée aux membres VIP. Connectez-vous pour continuer."}</p>
            <div className="login-modal-buttons">
              <button className="btn-login" onClick={() => window.location.href = "/login"}>Se connecter</button>
              <button className="btn-register" onClick={() => window.location.href = "/register"}>Créer un compte</button>
            </div>
            <button className="close-modal" onClick={() => setShowLoginModal(false)}>Fermer</button>
          </div>
        </div>
      )}

      {showVipModal && (
        <div className="login-modal-overlay">
          <div className="login-modal">
            <div className="login-modal-icon">🔒</div>
            <h2>Analyse VIP</h2>
            <p>Cette analyse est réservée aux membres VIP. Passez au VIP pour accéder aux analyses détaillées, signaux premium et alertes avancées.</p>
            <div className="login-modal-buttons">
              <button className="btn-login" onClick={() => window.location.href = "/vip"}>Devenir VIP</button>
            </div>
            <button className="close-modal" onClick={() => setShowVipModal(false)}>Fermer</button>
          </div>
        </div>
      )}

      {showStatsModal && selectedMatch && (
        <MatchStatsModal match={selectedMatch} onClose={closeStats} />
      )}

      <button 
        className={`floating-bell-btn ${buttonEffect.until > Date.now() ? `flash-${buttonEffect.type}` : ""}`} 
        onClick={() => setShowMobileSignals(true)}
      >
        <FontAwesomeIcon icon={faBell} />
      </button>
    </div>
  );
}

export default PublicMatchesPage;
